// ============================================================================
// APP - Main Application Component with Authentication
// ============================================================================

import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { BreakGlassProvider } from './contexts/BreakGlassContext';
import { BreakGlassGate } from './components/break-glass/BreakGlassGate';
import { TemplateProvider } from './contexts/TemplateContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AdminLayout } from './components/AdminLayout';
import { setAccessToken, setAuthFunctions } from './services/api';
import { HelpProvider } from './components/help/HelpContext';
import { HelpButton } from './components/help/HelpButton';
import { HelpPanel } from './components/help/HelpPanel';
import { TrainingModeProvider } from './training/TrainingModeContext';
import { TrainingSpotlight } from './training/TrainingSpotlight';
import { Toaster } from 'sonner';
import { useTicketLogger } from './hooks/useTicketLogger';
import { SelectionOverlay } from './components/ticket-logger/SelectionOverlay';
import { TicketLoggerModal } from './components/ticket-logger/TicketLoggerModal';

// Pages
import SessionCreator from './pages/SessionCreator';
import LiveSession from './pages/LiveSession';
import AvatarCatalog from './pages/AvatarCatalog';
import CreateAvatar from './pages/CreateAvatar';
import EditAvatar from './pages/EditAvatar';
import AvatarCognitionGraphPage from './pages/AvatarCognitionGraphPage';
import AvatarReviewQueue from './pages/AvatarReviewQueue';
import AvatarGroupManagement from './pages/AvatarGroupManagement';
import TemplateReviewQueue from './pages/TemplateReviewQueue';
import CreateAvatarGroup from './pages/CreateAvatarGroup';
import EditAvatarGroup from './pages/EditAvatarGroup';
import TemplateCatalog from './pages/TemplateCatalog';
import PhaseTemplateCatalog from './pages/PhaseTemplateCatalog';
import PhaseTemplateBuilder from './pages/PhaseTemplateBuilder';
import PhaseTemplateDetail from './pages/PhaseTemplateDetail';
import TemplateBuilder from './pages/TemplateBuilder';
import TemplatePreviewPage from './pages/TemplatePreview';
import DemoTemplateCatalog from './pages/DemoTemplateCatalog';
import DemoTemplateBuilder from './pages/DemoTemplateBuilder';
import UserManagement from './pages/UserManagement';
import McpStatusAdmin from './pages/admin/McpStatusAdmin';
import SecurityEvents from './pages/admin/SecurityEvents';
import LoginPage from './pages/LoginPage';
import AuthCallback from './pages/AuthCallback';
import UnauthorizedPage from './pages/UnauthorizedPage';
import ProjectsDashboard from './pages/ProjectsDashboard';
import ProjectDetail from './pages/ProjectDetail';
import AuditLogViewer from './pages/AuditLogViewer';
import NotificationsPage from './pages/Notifications/NotificationsPage';
import UserProfile from './pages/UserProfile';
import ReviewsPage from './pages/ReviewsPage';
import BuildWithAI from './pages/BuildWithAI';
import SecondScreenPage from './pages/SecondScreenPage';
import LLMCostsAdmin from './pages/LLMCostsAdmin';
import AnalyticsDashboard from './pages/AnalyticsDashboard';
import ActiveSessions from './pages/admin/ActiveSessions';
import OpsHealth from './pages/admin/OpsHealth';
import TtsVoicesAdmin from './pages/admin/TtsVoicesAdmin';
import SoundLibraryAdmin from './pages/admin/SoundLibraryAdmin';
import ConsentWordingAdmin from './pages/admin/ConsentWordingAdmin';
import RolesAdmin from './pages/admin/RolesAdmin';
import FleetManagement from './pages/FleetManagement';
import RoomManagement from './pages/RoomManagement';
import StudioBookingForm from './pages/StudioBookingForm';
import MyBookings from './pages/MyBookings';
import ReviewQueue from './pages/ReviewQueue';
import AdminFacilitators from './pages/AdminFacilitators';
import FacilitatorAvailability from './pages/FacilitatorAvailability';
import AdminEventTypes from './pages/AdminEventTypes';
import AdminBookingEmails from './pages/AdminBookingEmails';
import AdminSectors from './pages/AdminSectors';
import StudioCalendar from './pages/StudioCalendar';
import StudioCrm from './pages/StudioCrm';
import WorkflowConfigAdmin from './pages/WorkflowConfigAdmin';
import ParticipantJoin from './pages/Participant/ParticipantJoin';
import SessionHubPage from './pages/SessionHub/SessionHubPage';
import ParticipantView from './pages/Participant/ParticipantView';
import REACHProjectHome from './pages/reach/REACHProjectHome';
import EngagementConfigPage from './pages/reach/EngagementConfigPage';
import StakeholderMapPage from './pages/reach/StakeholderMapPage';
import AssessmentsPage from './pages/reach/AssessmentsPage';
import ResultsDashboardPage from './pages/reach/ResultsDashboardPage';
import { WorkbookPage } from './pages/reach/WorkbookPage';
import CommsLogPage from './pages/reach/CommsLogPage';
import BlockersPage from './pages/reach/BlockersPage';
import AdoptionPlanPage from './pages/reach/AdoptionPlanPage';
import CloseOutPage from './pages/reach/CloseOutPage';
import { REACHFeatureGuard } from './components/reach/common/REACHFeatureGuard';
import BidProjectHome from './pages/bid/BidProjectHome';
import { BidFeatureGuard } from './components/bid/common/BidFeatureGuard';
import TabletopProjectHome from './pages/tabletop/TabletopProjectHome';
import ExerciseSeedPage from './pages/tabletop/ExerciseSeedPage';
import { TabletopFeatureGuard } from './components/tabletop/common/TabletopFeatureGuard';
import Showcase from './pages/Showcase';
import ShowcaseDetail from './pages/ShowcaseDetail';
import StudioTv from './pages/StudioTv';

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 120000, // 2 minutes — reduces unnecessary refetches for semi-static data
    },
  },
});

function ConditionalTemplateProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const { isGuestOnly } = useAuth();
  if (location.pathname.startsWith('/p/')) return <>{children}</>;
  // Guests are refused the template catalogues and reach no screen that reads
  // them, so mounting the provider only loops 403s. Its consumers are
  // SessionCreator and CreateSessionTab, neither of which a guest can open.
  if (isGuestOnly) return <>{children}</>;
  return <TemplateProvider>{children}</TemplateProvider>;
}

/**
 * App Layout
 * Routes admin/catalog pages through AdminLayout with left sidebar,
 * while session/project/auth pages use their own layouts.
 */
function AppLayout({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const { isAuthenticated } = useAuth();

  const isAuthPage = ['/login', '/auth/callback', '/unauthorized'].includes(location.pathname);
  const isSessionPage = location.pathname.startsWith('/session/');
  const isSecondScreenPage = location.pathname.startsWith('/second-screen/');
  const isParticipantPage = location.pathname.startsWith('/p/');
  const isProjectDetailPage = location.pathname.match(/^\/projects\/[^/]+/);
  const isProfilePage = location.pathname === '/profile';

  // Pages that get the AdminLayout sidebar wrapper (everything except auth, sessions, second-screen, project detail, and profile)
  const needsAdminLayout = isAuthenticated && !isAuthPage && !isSessionPage && !isSecondScreenPage && !isProjectDetailPage && !isProfilePage && !isParticipantPage;

  if (needsAdminLayout) {
    return <AdminLayout>{children}</AdminLayout>;
  }

  return <>{children}</>;
}

/**
 * Admin ticket logger — Ctrl+Shift+T to select any element and log to ADO.
 * Only active with Admin::RaiseTickets. Mounted at root so it works on all pages.
 */
function TicketLogger() {
  const { user, can } = useAuth();
  const { mode, selected, screenshotBase64, setSelected, cancelSelecting, closeForm } = useTicketLogger();

  if (!user || !can('Admin::RaiseTickets')) return null;

  return (
    <>
      {mode === 'selecting' && (
        <SelectionOverlay onSelect={setSelected} onCancel={cancelSelecting} />
      )}
      {mode === 'form' && selected && (
        <TicketLoggerModal
          selected={selected}
          screenshotBase64={screenshotBase64}
          reportedBy={user.email || user.displayName || 'unknown'}
          onClose={closeForm}
        />
      )}
    </>
  );
}

/**
 * API Service Connector
 * Connects auth context to API service for token injection
 */
function ApiServiceConnector() {
  const { accessToken, refreshToken, logout } = useAuth();

  useEffect(() => {
    // Update token in API service whenever it changes
    setAccessToken(accessToken);
    // Token synced to API service
  }, [accessToken]);

  useEffect(() => {
    // Set auth functions once on mount
    setAuthFunctions(refreshToken, logout);
  }, [refreshToken, logout]);

  return null;
}

/**
 * App Routes
 */
function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/unauthorized" element={<UnauthorizedPage />} />

      {/* Participant View — no auth, own QR token */}
      <Route path="/p/:token" element={<ParticipantJoin />} />
      <Route path="/p/:token/session" element={<ParticipantView />} />

      {/* Session Hub — no auth, link token in the URL fragment + emailed code */}
      <Route path="/hub" element={<SessionHubPage />} />

      {/* Protected Routes - All Users */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <ProjectsDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/session/:id"
        element={
          <ProtectedRoute>
            <BreakGlassGate type="session" idParam="id">
              <LiveSession />
            </BreakGlassGate>
          </ProtectedRoute>
        }
      />
      <Route
        path="/second-screen/:id"
        element={
          <ProtectedRoute>
            <BreakGlassGate type="session" idParam="id">
              <SecondScreenPage />
            </BreakGlassGate>
          </ProtectedRoute>
        }
      />

      {/* Showcase Catalog - All Auth Users */}
      <Route
        path="/showcase"
        element={
          <ProtectedRoute guestAllowed>
            <Showcase />
          </ProtectedRoute>
        }
      />
      <Route
        path="/showcase/:showcaseId"
        element={
          <ProtectedRoute guestAllowed>
            <ShowcaseDetail />
          </ProtectedRoute>
        }
      />

      {/* Studio TV - fortnightly roundups, guests included */}
      <Route
        path="/studio-tv"
        element={
          <ProtectedRoute requiredPermission={'StudioTv::View'} guestAllowed>
            <StudioTv />
          </ProtectedRoute>
        }
      />
      <Route
        path="/studio-tv/:id"
        element={
          <ProtectedRoute requiredPermission={'StudioTv::View'} guestAllowed>
            <StudioTv />
          </ProtectedRoute>
        }
      />

      {/* User Profile - All Users */}
      <Route
        path="/profile"
        element={
          <ProtectedRoute guestAllowed>
            <UserProfile />
          </ProtectedRoute>
        }
      />

      {/* Protected Routes - Not Read-Only */}
      <Route
        path="/create"
        element={
          <ProtectedRoute requiredPermission={'Workspace::Write'}>
            <SessionCreator />
          </ProtectedRoute>
        }
      />

      {/* Projects - All Users */}
      <Route
        path="/projects"
        element={
          <ProtectedRoute>
            <ProjectsDashboard />
          </ProtectedRoute>
        }
      />
      {/* REACH Module nested inside ProjectDetail shell */}
      <Route
        path="/projects/:projectId"
        element={
          <ProtectedRoute>
            <BreakGlassGate type="project" idParam="projectId">
              <ProjectDetail />
            </BreakGlassGate>
          </ProtectedRoute>
        }
      >
        <Route path="reach" element={<REACHFeatureGuard><REACHProjectHome /></REACHFeatureGuard>} />
        <Route path="reach/config" element={<REACHFeatureGuard><EngagementConfigPage /></REACHFeatureGuard>} />
        <Route path="reach/stakeholder-map" element={<REACHFeatureGuard><StakeholderMapPage /></REACHFeatureGuard>} />
        <Route path="reach/assessments" element={<REACHFeatureGuard><AssessmentsPage /></REACHFeatureGuard>} />
        <Route path="reach/results" element={<REACHFeatureGuard><ResultsDashboardPage /></REACHFeatureGuard>} />
        <Route path="reach/workbook" element={<REACHFeatureGuard><WorkbookPage /></REACHFeatureGuard>} />
        <Route path="reach/comms" element={<REACHFeatureGuard><CommsLogPage /></REACHFeatureGuard>} />
        <Route path="reach/blockers" element={<REACHFeatureGuard><BlockersPage /></REACHFeatureGuard>} />
        <Route path="reach/plan" element={<REACHFeatureGuard><AdoptionPlanPage /></REACHFeatureGuard>} />
        <Route path="reach/closeout" element={<REACHFeatureGuard><CloseOutPage /></REACHFeatureGuard>} />
        <Route path="bid" element={<BidFeatureGuard><BidProjectHome /></BidFeatureGuard>} />
        <Route path="tabletop" element={<TabletopFeatureGuard><TabletopProjectHome /></TabletopFeatureGuard>} />
        <Route path="tabletop/exercises/new" element={<TabletopFeatureGuard><ExerciseSeedPage /></TabletopFeatureGuard>} />
        <Route path="tabletop/exercises/:exerciseId" element={<TabletopFeatureGuard><ExerciseSeedPage /></TabletopFeatureGuard>} />
      </Route>
      {/* Avatar Catalog - Avatar Admin and System Admin Only */}
      <Route
        path="/avatars"
        element={
          <ProtectedRoute requiredPermission={'Avatars::ManageAnyDefinition'}>
            <AvatarCatalog />
          </ProtectedRoute>
        }
      />

      {/* Avatar Creation - Not Read-Only */}
      <Route
        path="/avatars/create"
        element={
          <ProtectedRoute requiredPermission={'Workspace::Write'}>
            <CreateAvatar />
          </ProtectedRoute>
        }
      />
      <Route
        path="/avatars/:avatarId/edit"
        element={
          <ProtectedRoute requiredPermission={'Workspace::Write'}>
            <EditAvatar />
          </ProtectedRoute>
        }
      />
      <Route
        path="/avatars/:avatarId/cognition-graph"
        element={
          <ProtectedRoute requiredPermission={'Workspace::Write'}>
            <AvatarCognitionGraphPage />
          </ProtectedRoute>
        }
      />

      {/* Avatar Administration - Avatar Admin and System Admin Only */}
      <Route
        path="/avatars/review-queue"
        element={
          <ProtectedRoute requiredPermission={'Avatars::ReviewCatalog'}>
            <AvatarReviewQueue />
          </ProtectedRoute>
        }
      />

      {/* Template Administration - Template Admin and System Admin Only */}
      <Route
        path="/templates/review-queue"
        element={
          <ProtectedRoute requiredPermission={'Templates::Review'}>
            <TemplateReviewQueue />
          </ProtectedRoute>
        }
      />
      <Route
        path="/avatars/groups"
        element={
          <ProtectedRoute requiredPermission={'Avatars::ManageGroups'}>
            <AvatarGroupManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/avatars/groups/create"
        element={
          <ProtectedRoute requiredPermission={'Avatars::ManageGroups'}>
            <CreateAvatarGroup />
          </ProtectedRoute>
        }
      />
      <Route
        path="/avatars/groups/:groupId/edit"
        element={
          <ProtectedRoute requiredPermission={'Avatars::ManageGroups'}>
            <EditAvatarGroup />
          </ProtectedRoute>
        }
      />

      {/* Template Management - All Non-Read-Only Users */}
      <Route
        path="/templates"
        element={
          <ProtectedRoute requiredPermission={'Workspace::Write'}>
            <TemplateCatalog />
          </ProtectedRoute>
        }
      />
      <Route
        path="/templates/create"
        element={
          <ProtectedRoute requiredPermission={'Workspace::Write'}>
            <TemplateBuilder />
          </ProtectedRoute>
        }
      />
      <Route
        path="/templates/:templateId/preview"
        element={
          <ProtectedRoute requiredPermission={'Workspace::Write'}>
            <TemplatePreviewPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/templates/:templateId/edit"
        element={
          <ProtectedRoute requiredPermission={'Workspace::Write'}>
            <TemplateBuilder />
          </ProtectedRoute>
        }
      />

      {/* Demonstrator Templates - Admin (TEMPLATE_ADMIN/SYSTEM_ADMIN) only */}
      <Route
        path="/admin/demo-templates"
        element={
          <ProtectedRoute requiredPermission={'Templates::ManageDemonstrators'}>
            <DemoTemplateCatalog />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/demo-templates/create"
        element={
          <ProtectedRoute requiredPermission={'Templates::ManageDemonstrators'}>
            <DemoTemplateBuilder />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/demo-templates/:templateId/edit"
        element={
          <ProtectedRoute requiredPermission={'Templates::ManageDemonstrators'}>
            <DemoTemplateBuilder />
          </ProtectedRoute>
        }
      />

      <Route
        path="/templates/phases"
        element={
          <ProtectedRoute requiredPermission={'Workspace::Write'}>
            <PhaseTemplateCatalog />
          </ProtectedRoute>
        }
      />
      <Route
        path="/templates/phases/create"
        element={
          <ProtectedRoute requiredPermission={'Workspace::Write'}>
            <PhaseTemplateBuilder />
          </ProtectedRoute>
        }
      />
      <Route
        path="/templates/phases/:phaseTemplateId"
        element={
          <ProtectedRoute requiredPermission={'Workspace::Write'}>
            <PhaseTemplateDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/templates/phases/:phaseTemplateId/edit"
        element={
          <ProtectedRoute requiredPermission={'Workspace::Write'}>
            <PhaseTemplateBuilder />
          </ProtectedRoute>
        }
      />

      {/* Build with AI - All authenticated users */}
      <Route
        path="/build"
        element={
          <ProtectedRoute>
            <BuildWithAI />
          </ProtectedRoute>
        }
      />

      {/* Unified Reviews Page */}
      <Route
        path="/admin/reviews"
        element={
          <ProtectedRoute requiredPermission={['Avatars::ReviewCatalog', 'Templates::Review']}>
            <ReviewsPage />
          </ProtectedRoute>
        }
      />

      {/* System Admin Routes */}
      <Route
        path="/admin/fleet"
        element={
          <ProtectedRoute requiredPermission={'Admin::ManageWorkflowConfig'}>
            <FleetManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/rooms"
        element={
          <ProtectedRoute requiredPermission={'Admin::ManageRooms'}>
            <RoomManagement />
          </ProtectedRoute>
        }
      />
      {/* Studio Booking — all authenticated users */}
      <Route path="/studio/book" element={<ProtectedRoute guestAllowed><StudioBookingForm /></ProtectedRoute>} />
      <Route path="/studio/bookings" element={<ProtectedRoute guestAllowed><MyBookings /></ProtectedRoute>} />
      {/* Studio Booking — Tier 1 facilitator + system admin */}
      <Route
        path="/studio/review"
        element={
          <ProtectedRoute requiredPermission={'Studio::ReviewBookings'}>
            <ReviewQueue />
          </ProtectedRoute>
        }
      />
      <Route
        path="/studio/calendar"
        element={
          <ProtectedRoute guestAllowed>
            <StudioCalendar />
          </ProtectedRoute>
        }
      />
      <Route
        path="/studio/availability"
        element={
          <ProtectedRoute requiredPermission={'Facilitation::ManageAvailability'}>
            <FacilitatorAvailability />
          </ProtectedRoute>
        }
      />
      {/* Studio CRM — Tier 1 + Tier 2 facilitators + system admin */}
      <Route
        path="/studio/crm"
        element={
          <ProtectedRoute requiredPermission={'Studio::ManageCrm'}>
            <StudioCrm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/facilitators"
        element={
          <ProtectedRoute requiredPermission={'Facilitation::ViewReport'}>
            <AdminFacilitators />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/consent-wording"
        element={
          <ProtectedRoute requiredPermission={'Admin::ManageConsentTemplates'}>
            <ConsentWordingAdmin />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/booking-emails"
        element={
          <ProtectedRoute requiredPermission={'Studio::ManageBookingEmails'}>
            <AdminBookingEmails />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/event-types"
        element={
          <ProtectedRoute requiredPermission={'Studio::ManageEventTypes'}>
            <AdminEventTypes />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/sectors"
        element={
          <ProtectedRoute requiredPermission={'Studio::ManageSectors'}>
            <AdminSectors />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/llm-costs"
        element={
          <ProtectedRoute requiredPermission={'Admin::ViewAnalytics'}>
            <LLMCostsAdmin />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/analytics"
        element={
          <ProtectedRoute requiredPermission={'Admin::ViewAnalytics'}>
            <AnalyticsDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute requiredPermission={'Admin::ManageUsers'}>
            <UserManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/security-events"
        element={
          <ProtectedRoute requiredPermission={'Admin::ViewAudit'}>
            <SecurityEvents />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/mcp-status"
        element={
          <ProtectedRoute requiredPermission={'Admin::ViewAnalytics'}>
            <McpStatusAdmin />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/workflow-config"
        element={
          <ProtectedRoute requiredPermission={'Admin::ManageWorkflowConfig'}>
            <WorkflowConfigAdmin />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/sessions"
        element={
          <ProtectedRoute requiredPermission={'Admin::ManageSessions'}>
            <ActiveSessions />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/ops"
        element={
          <ProtectedRoute requiredPermission={'Admin::ViewAnalytics'}>
            <OpsHealth />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/voices"
        element={
          <ProtectedRoute requiredPermission={'Admin::ManageVoices'}>
            <TtsVoicesAdmin />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/sound-library"
        element={
          <ProtectedRoute requiredPermission={'Admin::ManageSoundLibrary'}>
            <SoundLibraryAdmin />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/roles"
        element={
          <ProtectedRoute requiredPermission={'Admin::ManageRoles'}>
            <RolesAdmin />
          </ProtectedRoute>
        }
      />
      {/* Compliance Routes */}
      <Route
        path="/compliance/audit-logs"
        element={
          <ProtectedRoute requiredPermission={'Admin::ViewAudit'}>
            <AuditLogViewer />
          </ProtectedRoute>
        }
      />
      <Route
        path="/notifications"
        element={
          <ProtectedRoute>
            <NotificationsPage />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

/**
 * Main App Component
 */
export default function App() {
  return (
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <ConditionalTemplateProvider>
            <HelpProvider>
              <TrainingModeProvider>
                <ApiServiceConnector />
                <Toaster position="bottom-right" richColors closeButton />
                <BreakGlassProvider>
                  <AppLayout>
                    <AppRoutes />
                  </AppLayout>
                </BreakGlassProvider>
                <HelpButton />
                <HelpPanel />
                <TrainingSpotlight />
                <TicketLogger />
              </TrainingModeProvider>
            </HelpProvider>
          </ConditionalTemplateProvider>
        </AuthProvider>
      </QueryClientProvider>
    </BrowserRouter>
  );
}
